// src/howto/guide.js — „Покажи ми": води човека бутон по бутон по живия екран.
//
// Защо така, а не с картинки: картинките остаряват при всяка промяна, а
// хората, за които е това, не четат — търсят какво да натиснат. Затова
// оцветяваме ИСТИНСКИЯ бутон (клас .guide-target) и чакаме човекът да го
// натисне; карта долу казва с едно изречение какво става.
//
// Картата стои в <body>, извън #app: отворен прозорец (modal.js) прави #app
// inert и тя би станала ненатискаема, а renderShell() чисти #app изцяло.

import { el } from '../shared/dom.js';
import { toast } from '../shared/toast.js';

const POLL_MS = 300;
// След толкова време без намерен бутон казваме защо може да го няма —
// по-рано би плашило, докато екранът още се зарежда.
const LOST_MS = 4000;
// Записът минава през сървъра — изчакваме да се затвори прозорецът.
const FINISH_MS = 1500;

let _run = null; // текущото водене (само едно наведнъж)

const norm = (s) => String(s || '').replace(/\s+/g, ' ').trim();
// Падащите бутони носят „▾" (калкулаторите) — в инструкцията пише само името.
const btnText = (n) => norm(n.textContent).replace(/\s*▾$/, '');

// Видимо ли е: без offsetParent/getClientRects, защото в jsdom те са винаги
// празни (CLAUDE.md → „jsdom") — проверката би минавала, без да проверява.
function shown(node) {
  for (let n = node; n && n.nodeType === 1; n = n.parentElement) {
    if (n.hidden) return false;
    // Свита секция (<details> без open): заглавието се вижда, съдържанието — не.
    if (n.tagName === 'DETAILS' && !n.open && n !== node) {
      const sum = n.querySelector(':scope > summary');
      if (!sum || !sum.contains(node)) return false;
    }
    const cs = (n.ownerDocument.defaultView || window).getComputedStyle(n);
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
  }
  return true;
}

// Къде да търсим: в най-горния отворен прозорец, ако има — бутонът „Запази"
// на формата, не някой друг отдолу. Иначе — целият екран.
function scopeOf(doc) {
  const root = doc.getElementById('modal-root');
  const top = root && root.lastElementChild;
  return top || doc.getElementById('app') || doc.body;
}

// Намира елемента за стъпката (или null). Изнесено за тестовете.
export function findTarget(t, doc = document) {
  if (!t) return null;
  const scope = scopeOf(doc);
  if (t.btn) {
    const want = norm(t.btn);
    // summary — заглавията на свитите секции (Мои фирми → „Банка").
    return [...scope.querySelectorAll('button, label.btn, a.btn, summary, [role="button"]')]
      .find((n) => btnText(n) === want && !n.disabled && shown(n)) || null;
  }
  if (t.field) {
    const want = norm(t.field);
    const lbl = [...scope.querySelectorAll('.field > label')].find((n) => norm(n.textContent) === want && shown(n));
    return lbl ? (lbl.closest('.field') || lbl) : null;
  }
  if (t.row) {
    // Целият списък, не първият ред: човекът избира СВОЯТА фактура.
    const row = [...scope.querySelectorAll('tr[role="button"]')].find(shown);
    return row ? (row.closest('table') || row) : null;
  }
  if (t.sel) {
    return [...scope.querySelectorAll(t.sel)].find(shown) || null;
  }
  return null;
}

function emptyText(doc) {
  const e = [...scopeOf(doc).querySelectorAll('.empty p')].find(shown);
  return e ? norm(e.textContent) : '';
}

const errCount = (doc) => doc.querySelectorAll('#toasts .toast.err').length;

function mark(node) {
  if (!node) return;
  node.classList.add('guide-target');
  try { node.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) { /* jsdom */ }
}
function unmark(node) { if (node) node.classList.remove('guide-target'); }

// Кое натискане върти стъпката: при „ред" — натискане на кой да е ред.
function isHit(step, target, ev) {
  if (!target || !ev || !ev.target || !target.contains(ev.target)) return false;
  if (step.t && step.t.row) return !!(ev.target.closest && ev.target.closest('tr[role="button"]'));
  return true;
}

export function stopGuide() {
  if (!_run) return;
  const r = _run;
  _run = null;
  clearInterval(r.timer);
  unmark(r.target);
  r.doc.removeEventListener('click', r.onClick, true);
  r.doc.removeEventListener('keydown', r.onKey, true);
  if (r.card && r.card.parentNode) r.card.parentNode.removeChild(r.card);
}

export function guideRunning() { return !!_run; }

// task: от TASKS; go(view): отваря екрана (shell подава renderShell).
export function startGuide(task, { go, doc = document } = {}) {
  stopGuide();
  const run = { task, i: 0, target: null, since: 0, doc, timer: null, card: null, onClick: null, onKey: null };
  _run = run;

  const stepNo = el('div', { class: 'guide-no' });
  const say = el('p', { class: 'guide-say' });
  const more = el('p', { class: 'guide-more', hidden: '' });
  const note = el('p', { class: 'guide-note' });
  const back = el('button', { class: 'btn ghost', type: 'button', text: '← Назад', onClick: () => goTo(run.i - 1) });
  const next = el('button', { class: 'btn', type: 'button', onClick: () => goTo(run.i + 1) });
  const stop = el('button', { class: 'btn ghost', type: 'button', text: '✕ Спри', onClick: () => stopGuide() });
  // role=region + aria-live: четецът прочита новата стъпка сам, без да
  // краде фокуса от формата, която човекът попълва.
  run.card = el('div', { class: 'guide-card', role: 'region', 'aria-label': 'Покажи ми: ' + task.title },
    el('div', { class: 'guide-head' }, el('span', { class: 'guide-title', text: task.title }), stepNo),
    el('div', { 'aria-live': 'polite' }, say, more, note),
    el('div', { class: 'guide-actions' }, back, next, stop));
  // Къде живее картата: в отворения прозорец, ако има. modal.js върти Tab
  // само вътре в прозореца (trapTab) и маркира всичко извън него като
  // „отвън" (aria-modal) — картата в <body> ставаше недостижима с клавиатура
  // точно на стъпката „попълнете и натиснете Готово" (ревю на Codex, 18.10).
  // Затвори ли се прозорецът, картата пада с него — tick() я връща в <body>.
  function home() {
    const root = doc.getElementById('modal-root');
    const want = (root && root.lastElementChild) || doc.body;
    if (run.card.parentNode !== want) want.appendChild(run.card);
  }
  home();

  function finish() {
    stopGuide();
    toast('Готово! 👍');
  }

  function goTo(i) {
    if (_run !== run) return;
    if (i >= task.steps.length) { finish(); return; }
    unmark(run.target);
    run.target = null;
    run.i = Math.max(0, i);
    run.since = Date.now();
    const st = task.steps[run.i];
    const last = run.i === task.steps.length - 1;
    stepNo.textContent = 'Стъпка ' + (run.i + 1) + ' от ' + task.steps.length;
    say.textContent = st.say;
    more.textContent = st.more || '';
    more.hidden = !st.more;
    note.textContent = '';
    back.hidden = run.i === 0;
    // Без бутон за натискане — човекът сам казва „готово".
    next.textContent = last ? 'Край ✓' : (st.t && st.done !== 'next' ? 'Прескочи →' : 'Готово, напред →');
    next.classList.toggle('ghost', !!(st.t && st.done !== 'next' && !last));
    tick();
  }

  function tick() {
    if (_run !== run) return;
    home();
    const st = task.steps[run.i];
    if (!st.t) return;
    // Екранът се пречертава (renderShell, нов прозорец) — намираме бутона
    // наново, щом старият изчезне.
    if (run.target && !run.target.isConnected) { run.target = null; }
    if (!run.target) {
      run.target = findTarget(st.t, doc);
      if (run.target) { mark(run.target); note.textContent = ''; }
      else if (Date.now() - run.since > LOST_MS) {
        // Празен екран („Още няма доставки…") — казваме неговите думи, а не
        // „затворете прозореца": там няма бутон, защото още няма записи.
        const empty = emptyText(doc);
        note.textContent = empty ? 'Тук още няма записи: „' + empty + '“' : st.t.row
          ? 'Списъкът е празен или не е на екрана. Изберете фирмата горе, ако не е избрана.'
          : 'Не виждам това на екрана. Ако има отворен друг прозорец — затворете го. Ако няма избрана фирма — изберете я горе.';
      }
    } else if (!run.target.classList.contains('guide-target')) {
      mark(run.target);
    }
  }

  run.onClick = (ev) => {
    if (_run !== run || (run.card && run.card.contains(ev.target))) return;
    // Прозорецът се отваря от самото натискане — картата отива в него
    // веднага, а не след до 300 ms (иначе първият Tab я подминава).
    setTimeout(() => { if (_run === run) home(); }, 0);
    const st = task.steps[run.i];
    if (!st.t || st.done === 'next') return;
    if (!isHit(st, run.target, ev)) return;
    const at = run.i;
    if (at === task.steps.length - 1) {
      // Последното „Запази" може да не мине (липсва клиент, грешна сума).
      // Познаваме го по червеното съобщение (toast.js): докато има такова
      // на екрана — оставаме на стъпката, вместо да изчезнем. (Еднаква
      // грешка не се показва втори път, затова гледаме „има ли", не „нова
      // ли е".) Прозорецът сам не казва нищо: някои остават отворени и след
      // успешен запис (колата).
      setTimeout(() => {
        if (_run !== run || run.i !== at) return;
        if (errCount(doc) > 0) { note.textContent = 'Има червено съобщение — направете каквото казва и натиснете пак. Или „Край ✓".'; return; }
        finish();
      }, FINISH_MS);
      return;
    }
    // Малко време екранът да отвори прозореца, преди да търсим следващото.
    setTimeout(() => { if (_run === run && run.i === at) goTo(at + 1); }, POLL_MS);
  };
  // capture: бутоните често спират събитието (stopPropagation) — менюто горе.
  doc.addEventListener('click', run.onClick, true);
  // Enter/интервал: истинските бутони сами пускат click, но редът от списъка
  // (clickableRow) и етикетът „Избери файл" се отварят направо от keydown —
  // без това воденето оставаше на „натиснете реда", макар документът да е
  // отворен (ревю на Codex, 18.10). Двойно придвижване няма: goTo се вика
  // само ако стъпката още е същата.
  run.onKey = (ev) => {
    if (ev.key !== 'Enter' && ev.key !== ' ') return;
    const t = ev.target;
    if (!t || !t.tagName || /^(BUTTON|SUMMARY|A|INPUT|SELECT|TEXTAREA)$/.test(t.tagName)) return;
    run.onClick(ev);
  };
  doc.addEventListener('keydown', run.onKey, true);
  run.timer = setInterval(tick, POLL_MS);

  if (go && task.view) go(task.view);
  goTo(0);
  return run;
}
